/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useState, useEffect } from 'react';

export type ConsentType = 'granted' | 'denied' | 'minimum' | null;

interface PrivacyContextType {
  hasConsent: boolean;
  consentType: ConsentType;
  grantConsent: () => void;
  grantMinimumConsent: () => void;
  revokeConsent: () => void;
  hasConsentBeenSet: boolean;
}

const PrivacyContext = createContext<PrivacyContextType | undefined>(undefined);

const CONSENT_KEY = 'ga-consent';
const CONSENT_SET_KEY = 'ga-consent-set';

// Initialize state from localStorage synchronously to avoid flash of banner
const getInitialConsentState = () => {
  if (typeof window === 'undefined') {
    return { hasConsent: false, consentType: null as ConsentType, hasConsentBeenSet: false };
  }

  const consent = localStorage.getItem(CONSENT_KEY) as ConsentType;
  const consentSet = localStorage.getItem(CONSENT_SET_KEY) === 'true';

  if (consent === 'granted' || consent === 'minimum') {
    return { hasConsent: true, consentType: consent, hasConsentBeenSet: consentSet };
  } else if (consent === 'denied') {
    return { hasConsent: false, consentType: 'denied' as ConsentType, hasConsentBeenSet: consentSet };
  }

  return { hasConsent: false, consentType: null as ConsentType, hasConsentBeenSet: consentSet };
};

export const PrivacyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const initialState = getInitialConsentState();
  const [hasConsent, setHasConsent] = useState(initialState.hasConsent);
  const [consentType, setConsentType] = useState<ConsentType>(initialState.consentType);
  const [hasConsentBeenSet, setHasConsentBeenSet] = useState(initialState.hasConsentBeenSet);

  useEffect(() => {
    const consent = localStorage.getItem(CONSENT_KEY) as ConsentType;
    const consentSet = localStorage.getItem(CONSENT_SET_KEY) === 'true';

    setHasConsentBeenSet(consentSet);

    if (consent === 'granted' || consent === 'minimum') {
      setHasConsent(true);
      setConsentType(consent);
    } else if (consent === 'denied') {
      setHasConsent(false);
      setConsentType('denied');
    }
  }, []);

  const grantConsent = () => {
    setHasConsent(true);
    setConsentType('granted');
    setHasConsentBeenSet(true);
    localStorage.setItem(CONSENT_KEY, 'granted');
    localStorage.setItem(CONSENT_SET_KEY, 'true');

    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: 'granted',
      });
    }
  };

  const grantMinimumConsent = () => {
    setHasConsent(true);
    setConsentType('minimum');
    setHasConsentBeenSet(true);
    localStorage.setItem(CONSENT_KEY, 'minimum');
    localStorage.setItem(CONSENT_SET_KEY, 'true');

    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: 'denied', // Minimum consent = no analytics storage
      });
    }
  };

  const revokeConsent = () => {
    setHasConsent(false);
    setConsentType('denied');
    setHasConsentBeenSet(true);
    localStorage.setItem(CONSENT_KEY, 'denied');
    localStorage.setItem(CONSENT_SET_KEY, 'true');

    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: 'denied',
      });
    }
  };

  return (
    <PrivacyContext.Provider
      value={{ hasConsent, consentType, grantConsent, grantMinimumConsent, revokeConsent, hasConsentBeenSet }}
    >
      {children}
    </PrivacyContext.Provider>
  );
};

export const usePrivacy = () => {
  const context = useContext(PrivacyContext);
  if (context === undefined) {
    throw new Error('usePrivacy must be used within a PrivacyProvider');
  }
  return context;
};
